import React, { useState } from 'react';
import { Button, InputPassword, sparkToast, useReadOnly } from '@spark';

import SettingsSectionCard from './SettingsSectionCard.jsx';
import EditableSecretInput from './EditableSecretInput.jsx';
import { useAuth } from '@/features/auth/AuthContext';
import TavilyLogo from '@/shared/assets/logos/tavily-full.svg?react';
import './settings.css';

// The section header reads "API Key" on the left and carries the Tavily
// wordmark on the right (``headerExtra``). ``role="img"`` + ``aria-label``
// give the logo an accessible name so screen readers still hear "Tavily".
const TAVILY_LOGO = (
    <TavilyLogo
        className="settings-brand-logo"
        role="img"
        aria-label="Tavily"
        data-testid="tavily-logo"
    />
);

function validateTavilyApiKey(apiKey) {
    const trimmed = (apiKey || '').trim();
    if (!trimmed) return 'Tavily API key cannot be empty';
    if (!trimmed.startsWith('tvly-')) return 'Tavily API key should start with "tvly-"';
    if (trimmed.length < 20) return 'API key appears to be too short';
    return null;
}

function getMaskedToken(token) {
    if (!token || token.length <= 6) return '*'.repeat(token ? token.length : 0);
    return token.substring(0, 6) + '...' + token.substring(token.length - 4);
}

/**
 * Tavily API Key tab: save/edit/delete key for dataset research,
 * restyled on the shared Spark section patterns (description with docs
 * link, 360px InputPassword with lock prefix, masked-token row).
 */
const TavilyApiKey = ({ apiKey, onApiKeyUpdate }) => {
    const { role, isAuthEnabled } = useAuth();
    const canEdit = !isAuthEnabled || role === 'Admin' || role === 'Editor';
    const { readOnly } = useReadOnly();
    const [newApiKey, setNewApiKey] = useState('');

    const handleAddApiKey = () => {
        const error = validateTavilyApiKey(newApiKey);
        if (error) {
            sparkToast.error(error);
            return;
        }
        onApiKeyUpdate(newApiKey.trim(), 'saved');
        setNewApiKey('');
    };

    return (
        <SettingsSectionCard title="API Key" headerExtra={TAVILY_LOGO}>
            <div className="settings-providers__panel">
                <p className="settings-text settings-providers__description fs-unmask">
                    Add your Tavily API key to let the retrieval agent research
                    and recommend relevant datasets from Hugging Face, Kaggle
                    and other sources. Need a Tavily API key? Get one
                    {' '}
                    <a
                        className="settings-link"
                        href="https://app.tavily.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        here
                    </a>
                </p>

                {apiKey ? (
                    <EditableSecretInput
                        value={apiKey}
                        placeholder="Enter your Tavily API key"
                        maskFn={getMaskedToken}
                        editable={canEdit}
                        confirmDeleteTitle="Delete this Tavily API key?"
                        confirmDeleteDescription="Dataset research via Tavily will no longer be available."
                        onSave={async (edited) => {
                            const error = validateTavilyApiKey(edited);
                            if (error) {
                                sparkToast.error(error);
                                // Reject so the editor stays open for a fix.
                                throw new Error(error);
                            }
                            onApiKeyUpdate(edited.trim(), 'edited');
                        }}
                        onDelete={canEdit ? () => onApiKeyUpdate(null, 'deleted') : undefined}
                    />
                ) : (
                    <div className="settings-keys__row">
                        <InputPassword
                            size="large"
                            showLabel={false}
                            showPrefixIcon
                            value={newApiKey}
                            onChange={(e) => setNewApiKey(e.target.value)}
                            placeholder="Enter your Tavily API key"
                            disabled={readOnly || !canEdit}
                            className="settings-secret__input"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleAddApiKey();
                            }}
                        />
                        {newApiKey.trim() ? (
                            <Button size="tiny" variant="ghost" disableWhenReadOnly onClick={handleAddApiKey}>
                                Save
                            </Button>
                        ) : null}
                    </div>
                )}
            </div>
        </SettingsSectionCard>
    );
};

export default TavilyApiKey;
